import { useCallback, useEffect, useState } from 'react';
import { LayerOption } from 'components';
import { useLayers } from 'hooks/useLayers';
import { useMap } from 'hooks/useMap';
import { ILayers } from 'contexts/@types/Layers';
import * as Styled from './styles';
import { useTranslation } from 'react-i18next';
import customSubtitles from './customSubtitles';
import { DetailsMinasModal } from '../DetailsMinasModal';
import { FeaturesModal } from '../FeaturesModal';
import { AnalytesModal } from '../AnalytesModal';
import { RiInformationFill, RiMenu2Fill, RiTestTubeFill } from 'react-icons/ri';
import { TbListDetails, TbMapSearch } from 'react-icons/tb';
import { useZIndex } from 'hooks/useZIndex';
import { ZIndexLayerId } from 'contexts/ZIndexContext';
import { getAnalytesData, getFirstAnalyteRecord } from 'utils/analytes';
import { getVectorLayerAttributes } from 'utils/vectorLayerAttributes';

export function ModalLayerOptions() {
  const {
    subTitles,
    openLayerFeaturesModal,
    openLayerDetailsModal,
    setOpenLayerDetailsModal,
    layersFeatures,
    addedLayers,
    setOpenLayerFeaturesModal,
    moveLayerFearuresModal,
  } = useMap();
  const { layersActived, subtitleLayerOpened, handleSubtitleLayerOpened, handleCloseSubtitles, handleCloseInformation, getLayerAttritubeById } = useLayers();
  const { t: translation } = useTranslation();
  const [subtitlesOpen, setSubtitlesOpen] = useState<ILayers[]>();
  const [informationsOpen, setInformations] = useState<ILayers[]>();
  const [index, setIndex] = useState<number>(0);
  const [resetFeatures, setResetFeatures] = useState<boolean>(false);
  const [resetDetails, setResetDetails] = useState<boolean>(false);
  const [analytesModalDismissed, setAnalytesModalDismissed] = useState<boolean>(false);
  const [moveLayerDetailsModal, setMoveLayerDetailsModal] = useState<boolean>(false);
  const { bringToFront, getZIndex } = useZIndex();

  const [mousePosition, setMousePosition] = useState({ posX: 0, posY: 0 });
  const [modalPosition, setModalPosition] = useState({ posX: 0, posY: 0 });
  const [modalDetailsPosition, setModalDetailsPosition] = useState({ posX: 0, posY: 0 });

  const [processos, setProcessos] = useState<string[]>([]);
  const selectedFeatureEntry = layersFeatures
    ? Array.from(layersFeatures.entries()).flatMap(([key, features]) =>
        features.map(feature => ({ key, feature })),
      )[index] as any
    : undefined;
  const selectedFeatureAttributes = selectedFeatureEntry?.key === 'user_layers'
    ? getVectorLayerAttributes(
        addedLayers.find(layer => layer.layertitle === selectedFeatureEntry.feature.layername) ?? {
          atributos: [],
          vector_source: undefined,
        },
      )
    : getLayerAttritubeById(Number(selectedFeatureEntry?.key?.split('&&')[1]))?.atributos ?? [];
  const analytesData = getAnalytesData(
    selectedFeatureAttributes,
    selectedFeatureEntry?.feature?.properties,
  );
  const firstAnalyte = getFirstAnalyteRecord(analytesData?.value);
  const analytesModalTitle = firstAnalyte?.amostra ?? 'Dados analíticos';
  const showAnalytesModal = openLayerFeaturesModal && !!analytesData && !analytesModalDismissed;

  const bringModalToFront = useCallback(
    (id: ZIndexLayerId) => {
      bringToFront(id);
    },
    [bringToFront],
  );

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!openLayerFeaturesModal) {
        // Prepara a posição do modal para ele não piscar na posição padrão ao abrir
        setModalPosition({
          posX: event.clientX,
          posY: event.clientY,
        });
      }
      if (!openLayerDetailsModal) {
        setModalDetailsPosition({
          posX: event.clientX,
          posY: event.clientY,
        });
      }
      // Atualiza apenas a posição do último clique
      // pois a posição só muda se o clique resultar em novas informações
      setMousePosition({
        posX: event.clientX,
        posY: event.clientY,
      });
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  });

  useEffect(() => {
    if (openLayerFeaturesModal) {
      bringModalToFront('map-features-modal');
      getProcessos();
    }
    setModalPosition(mousePosition);
    setResetFeatures(!resetFeatures);
  }, [moveLayerFearuresModal, openLayerFeaturesModal, bringModalToFront]);

  useEffect(() => {
    setAnalytesModalDismissed(false);
  }, [layersFeatures, index]);

  useEffect(() => {
    if (showAnalytesModal) {
      bringModalToFront('map-analytes-modal');
    }
  }, [showAnalytesModal, bringModalToFront]);

  useEffect(() => {
    if (openLayerDetailsModal) {
      bringModalToFront('map-details-modal');
    }
    setModalDetailsPosition(mousePosition);
    setResetDetails(!resetDetails);
  }, [moveLayerDetailsModal, openLayerDetailsModal, bringModalToFront]);

  function getPosX(details: boolean): number {
    const pos = details ? modalDetailsPosition.posX - (window.innerWidth >= 1280 ? 300 : 100) : modalPosition.posX;
    return Math.min(pos + 25, window.innerWidth - 280);
  }

  function getPosY(details: boolean): number {
    const pos = details ? modalDetailsPosition.posY + (window.innerWidth >= 1280 ? 0 : 50) : modalPosition.posY;
    return Math.min(pos - 150, window.innerHeight - 160);
  }

  function getAnalytesPosX(): number {
    const featuresPosX = getPosX(false);
    const rightPosition = featuresPosX + 275;

    if (rightPosition <= window.innerWidth - 280) return rightPosition;

    return Math.max(featuresPosX - 275, 0);
  }

  function getProcessos() {
    const result: string[] = [];
    if (layersFeatures) {
      layersFeatures.forEach((features, key) => {
        if (key.includes("minas_ativas")) {
          result.push(...features.map((f: any) => {
            if (f.properties.ds_processo_grp_min) {
              return f.properties.ds_processo_grp_min.replace('.', '');
            }
            else if (f.properties.ds_processo) {
              // Verificação para manter compatibilidade com método antigo
              return f.properties.ds_processo.replace('.', '');
            }
          }));
        }
      });
    }
    setProcessos(Array.from(new Set(result)));
    setOpenLayerDetailsModal(result.length > 0);
    setMoveLayerDetailsModal(move => !move)
  }

  useEffect(() => {
    if (subtitleLayerOpened) {
      handleSubtitleLayerOpened({} as ILayers);
    }
    getProcessos();
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [layersFeatures]);

  useEffect(() => {
    const layersActiveBySubtitle = layersActived.filter((layer: ILayers) => layer.subtitle_open);
    const layersActiveByInformation = layersActived.filter((layer: ILayers) => layer.information_open);
    setSubtitlesOpen(layersActiveBySubtitle);
    setInformations(layersActiveByInformation);
  }, [layersActived]);

  useEffect(() => {
    if (subtitlesOpen && subtitlesOpen.length > 0) {
      bringModalToFront('map-subtitles-modal');
    }
  }, [subtitlesOpen?.length, bringModalToFront]);

  useEffect(() => {
    if (informationsOpen && informationsOpen.length > 0) {
      bringModalToFront('map-informations-modal');
    }
  }, [informationsOpen?.length, bringModalToFront]);

  return (
    <>
      <Styled.Wrapper>
        {subtitlesOpen && subtitlesOpen.length > 0 && (
          <Styled.Features onClick={() => bringModalToFront('map-subtitles-modal')} zIndex={getZIndex('map-subtitles-modal')}>
            <LayerOption
              defaultPos={{x: 400, y: 200}}
              increaseIndex={() => bringModalToFront('map-subtitles-modal')}
              title={
                <>
                  <div id="btn-subtitle">
                    <RiMenu2Fill size={22} />
                  </div>
                  <p id="title-dragglable">{translation('mapPage.modalInformations.subtitle')}</p>
                </>
              }
              onClose={handleCloseSubtitles}
              index={0}
              pages={0}
              content={subtitlesOpen
                .sort((a: ILayers, b: ILayers) => {
                  return a.layertitle.localeCompare(b.layertitle, 'pt');
                })
                .map((item: ILayers) => {
                  const subTitleActived = subTitles.find(subTitle => subTitle.layername === item.layername);
                  if (!subTitleActived || !item.subtitle_open) return;

                  const customSubtitle = customSubtitles.find(custom => custom.layername === subTitleActived.layername);
                  if (customSubtitle) {
                    return (
                      <div key='item-layertitle'>
                        <h4>
                          <strong>{item.layertitle}</strong>
                        </h4>
                        {customSubtitle.icon}
                        <hr />
                      </div>
                    );
                  } else {
                    return (
                      <div key='item-layertitle'>
                        <h4>
                          <strong>{item.layertitle}</strong>
                        </h4>
                        <img src={subTitleActived.url} alt={subTitleActived.layername} />
                        <hr />
                      </div>
                    );
                  }
                })}
            />
          </Styled.Features>
        )}

        {informationsOpen && informationsOpen.length > 0 && (
          <Styled.Features onClick={() => bringModalToFront('map-informations-modal')} zIndex={getZIndex('map-informations-modal')}>
            <LayerOption
              defaultPos={{x: 800, y: 200}}
              increaseIndex={() => bringModalToFront('map-informations-modal')}
              title={
                <>
                  <div id="btn-subtitle">
                    <RiInformationFill size={22} />
                  </div>
                  <p id="title-dragglable">{translation('mapPage.modalInformations.information')}</p>
                </>
              }
              onClose={handleCloseInformation}
              index={0}
              pages={0}
              content={informationsOpen
                .sort((a: ILayers, b: ILayers) => {
                  return a.layertitle.localeCompare(b.layertitle, 'pt');
                })
                .map((item: ILayers) => {
                  return (
                    <>
                      <h4>
                        <strong>{item.layertitle}</strong>
                      </h4>

                      {item.metadados ? (
                        <>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.category')}:</strong>{' '}
                            {item.metadados.categoria || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.theme')}:</strong>{' '}
                            {item.metadados.tema || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.subtheme')}:</strong>{' '}
                            {item.metadados.subtema || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.summary')}:</strong>{' '}
                            {item.metadados.resumo || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.organization')}:</strong>{' '}
                            <span className='notranslate'>{item.metadados.organizacao || 'Não informado'}</span>
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.spatialRepresentation')}:</strong>{' '}
                            {item.metadados.representacao || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.date')}:</strong>{' '}
                            {item.metadados.data || 'Não informado'}
                          </p>
                          <p>
                            <strong>{translation('mapPage.sideBar.informations.linkMetadata')}:</strong>{' '}
                            <a
                              href={item.metadado_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {translation('mapPage.sideBar.informations.geonodeAccess')}
                            </a>
                          </p>
                          { item.metadados.leilao &&
                            <p>
                              <strong>Leilão:</strong>{' '}
                              <a
                                href={item.metadados.leilao}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Acessar
                              </a>
                            </p>
                          }
                        </>
                      ) : (
                        <p>{translation('mapPage.sideBar.informations.notFoundMetadados')}.</p>
                      )}

                      <hr />
                    </>
                  );
                })}
            />
          </Styled.Features>
        )}
        <Styled.Features onClick={() => bringModalToFront('map-features-modal')} zIndex={getZIndex('map-features-modal')}>
          {openLayerFeaturesModal && (
            <LayerOption
              defaultPos={{x: getPosX(false), y: getPosY(false)}}
              title={
                <>
                  <div id="btn-subtitle">
                    <TbMapSearch size={22} />
                  </div>
                  <p id="title-dragglable">{translation('mapPage.modalInformations.features')}</p>
                </>
              }
              onClose={() => {setOpenLayerFeaturesModal(false)}}
              increaseIndex={() => bringModalToFront('map-features-modal')}
              reset={resetFeatures}
              content={<FeaturesModal setIsOpen={setOpenLayerFeaturesModal} features={layersFeatures} index={index} />}
              index={index}
              setIndex={setIndex}
              pages={Array.from(layersFeatures?.values() ?? []).flat().length || 1}
              pageable
            />
          )}
        </Styled.Features>

        {showAnalytesModal && analytesData && (
          <Styled.Features onClick={() => bringModalToFront('map-analytes-modal')} zIndex={getZIndex('map-analytes-modal')}>
            <LayerOption
              defaultPos={{x: getAnalytesPosX(), y: getPosY(false)}}
              title={
                <>
                  <div id="btn-subtitle">
                    <RiTestTubeFill size={22} />
                  </div>
                  <p id="title-dragglable">{String(analytesModalTitle)}</p>
                </>
              }
              onClose={() => setAnalytesModalDismissed(true)}
              increaseIndex={() => bringModalToFront('map-analytes-modal')}
              reset={resetFeatures}
              content={<AnalytesModal analytes={analytesData.value} />}
              index={index}
              pages={0}
            />
          </Styled.Features>
        )}

        <Styled.Features onClick={() => bringModalToFront('map-details-modal')} zIndex={getZIndex('map-details-modal')}>
          {openLayerDetailsModal && (
            <LayerOption
              defaultPos={{x: getPosX(true), y: getPosY(true)}}
              onClose={() => {setOpenLayerDetailsModal(false)}}
              increaseIndex={() => bringModalToFront('map-details-modal')}
              reset={resetDetails}
              title={
                <>
                  <div id="btn-subtitle">
                    <TbListDetails size={22} />
                  </div>
                  <p id="title-dragglable">Detalhes</p>
                </>
              }
              content={
                <DetailsMinasModal processos={processos} />
              }
              index={index}
              setIndex={setIndex}
              pages={0}
            />
          )}
        </Styled.Features>
      </Styled.Wrapper>
    </>
  );
}
